import { useId } from "react";

const script = (id: string) => {
  const KEY = "store-cookie-consent";
  const HIDDEN = ["translate-y-6", "opacity-0", "pointer-events-none"];
  const SHOW_DELAY_MS = 1200;

  const read = () => {
    try {
      return localStorage.getItem(KEY);
    } catch {
      return "declined";
    }
  };
  const write = (value: string) => {
    try {
      localStorage.setItem(KEY, value);
    } catch { /* storage bloqueado: só fecha o banner */ }
  };

  const setup = () => {
    const elem = document.getElementById(id);
    if (!elem || read()) return;

    const hide = () => elem.classList.add(...HIDDEN);
    // Só a recusa muda algo: sem resposta, o padrão é medir visitas.
    elem.querySelector("[data-button-cc-decline]")?.addEventListener("click", () => {
      write("declined");
      hide();
    });
    elem.querySelector("[data-button-cc-accept]")?.addEventListener("click", () => {
      write("accepted");
      hide();
    });
    setTimeout(() => elem.classList.remove(...HIDDEN), SHOW_DELAY_MS);
  };

  if (document.readyState === "complete") setup();
  else addEventListener("load", setup, { once: true });
};

export interface Props {
  title?: string;
  /** @format html */
  text?: string;
  policy?: {
    text?: string;
    link?: string;
  };
  buttons?: {
    allowText: string;
    cancelText?: string;
  };
  layout?: {
    position?: "Expanded" | "Left" | "Center" | "Right";
    content?: "Tiled" | "Piled up";
  };
}

const DEFAULT_PROPS = {
  title: "Cookies",
  text:
    "Usamos cookies para medir visitas e mostrar ofertas relevantes. Você pode aceitar ou recusar.",
  policy: {
    text: "Política de privacidade",
    link: "/politica-de-privacidade",
  },
  buttons: {
    allowText: "Aceitar",
    cancelText: "Recusar",
  },
  layout: {
    position: "Expanded",
    content: "Tiled",
  },
};

function CookieConsent(props: Props) {
  const id = useId();
  const { title, text, policy, buttons, layout } = {
    ...DEFAULT_PROPS,
    ...props,
  };
  const position = layout?.position ?? "Expanded";
  const align = position === "Right"
    ? "lg:justify-end"
    : position === "Center"
    ? "lg:justify-center"
    : "lg:justify-start";

  return (
    <>
      <div
        id={id}
        role="dialog"
        aria-label={title}
        className={`translate-y-6 opacity-0 pointer-events-none transition duration-500 ease-out fixed bottom-0 left-0 right-0 z-50 px-3 pb-3 lg:px-6 lg:pb-6 flex ${align}`}
      >
        <div className="w-full lg:max-w-md bg-base-100 rounded-2xl shadow-xl border border-base-200 p-5 flex flex-col gap-3">
          <h3 className="text-base font-semibold tracking-wide">{title}</h3>
          {text && (
            <div
              className="text-sm leading-relaxed text-base-content/80"
              dangerouslySetInnerHTML={{ __html: text }}
            />
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            <a href={policy.link} className="text-xs underline underline-offset-2 text-base-content/70">
              {policy.text}
            </a>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                data-button-cc-decline
                className="btn btn-sm btn-outline rounded-full px-5 font-normal"
              >
                {buttons.cancelText}
              </button>
              <button
                type="button"
                data-button-cc-accept
                className="btn btn-sm btn-neutral rounded-full px-5 font-normal"
              >
                {buttons.allowText}
              </button>
            </div>
          </div>
        </div>
      </div>
      <script
        type="module"
        dangerouslySetInnerHTML={{ __html: `(${script})("${id}");` }}
      />
    </>
  );
}

export default CookieConsent;
